import {
  DESTINATION_OPTIONS,
  INTEGRATION_OPTIONS,
  OPSGENIE,
} from '@elementary/common-ui/constants/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OpsgenieIntegrationForm } from 'shared/types';
import { MutationError } from '@elementary/common-ui/types/mutationTypes';
import useSnackbar from '../useSnackbar';
import * as api from '../../services/settingsApi';
import useSendTracking from './useSendTracking';

type Props = {
  onUpdateSuccess?: () => void;
};

const useOpsgenieIntegration = ({ onUpdateSuccess }: Props) => {
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();

  const { tracking, trackingEvents, sendTrackingWithoutSensitiveData } =
    useSendTracking();

  const opsgenieQuery = useQuery([OPSGENIE], () => api.fetchOpsgenie(), {});

  const opsgenieIntegrationMutation = useMutation(
    (data: OpsgenieIntegrationForm) => {
      const reqData = {
        token: data?.token,
        regionApiUrl: data?.regionApiUrl,
      };

      return opsgenieQuery.data?.integrated
        ? api.updateOpsgenieIntegration(reqData)
        : api.setOpsgenieIntegration(reqData);
    },
    {
      onSuccess: () => {
        onUpdateSuccess?.();
        queryClient.invalidateQueries([OPSGENIE]);
        queryClient.invalidateQueries([DESTINATION_OPTIONS]);
        queryClient.invalidateQueries([INTEGRATION_OPTIONS]);
        openSnackbar({
          message: 'Successfully connected to your Opsgenie',
          variant: 'success',
        });
        tracking?.sendTrackingEvent({
          eventName: trackingEvents?.CONNECT_OPSGENIE_INTEGRATION_SUCCESSFULLY,
        });
      },
      onError: (error: MutationError) =>
        sendTrackingWithoutSensitiveData({
          error,
          eventName: trackingEvents?.CONNECT_OPSGENIE_INTEGRATION_FAILURE,
        }),
    },
  );

  return {
    opsgenieQuery,
    opsgenieIntegrationMutation,
    isOpsgenieConnected: !!opsgenieQuery.data?.integrated,
  };
};

export default useOpsgenieIntegration;
